import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WhiteWrapper, Wrapper, Button } from "../Common/styles.common";
import BackButton from "../Button/BackButton";
import ShowToast from "../Common/Toast";
import { PDFDocument } from "pdf-lib";
import { api } from "../../../network/network";
import { API_HOST_WMS } from "../../../config/api.config";
import { APIendpoints } from "../../../utils/constants/APIendpoints";
import { 
  ENTITY_TYPE_CREDIT_NOTE, 
  ENTITY_TYPE_INVOICE, 
  ERROR_CODE_BAD_REQUEST,
  USER_TYPE_VENDOR,
  MIME_TYPE_PDF,
  MIME_TYPE_IMAGE_JPEG,
  MIME_TYPE_IMAGE_JPG,
  MIME_TYPE_IMAGE_PNG,
  UPLOAD_TYPE_IMAGE,
  UPLOAD_TYPE_PDF,
} from "utils/constants/others";
import {
  Card,
  CardTitle,
  Dropdown,
  TextInput,
  UploadTilesContainer,
  UploadTile,
  UploadIcon,
  UploadLabel,
  FileName,
  FixedFooter,
  SubmitButton,
  ModalOverlay,
  ModalContent,
  CloseButton,
  SuccessIcon,
  ModalTitle,
  ModalButton,
  ErrorText,
  FilePreviewContainer,
  PreviewTitle,
  FilePreviewItem,
  ImagePreview,
  PdfPreviewIcon,
  FilePreviewInfo,
  PreviewFileName,
  PreviewFileSize,
  RemoveButton,
  ScrollableContent,
  ErrorModalContent,
  ErrorIconContainer,
  ErrorIcon,
  ErrorMessage,
  ContactInfoBox,
  ContactTitle,
  ContactDetail,
  ContactLabel,
  ContactValue,
  BackIcon,
  FileTypeLabel,
  ErrorIconImage,
} from "./styles.uploaddoc";

const UploadDocuments = () => {
  const { purchaseOrderNumber } = useParams();
  const navigate = useNavigate();

  // Form state
  const [documentType, setDocumentType] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [uploadType, setUploadType] = useState(null); // 'image' or 'pdf'

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    // Set upload type to image if not set
    if (!uploadType) {
      setUploadType(UPLOAD_TYPE_IMAGE);
    }

    const validFiles = [];
    let hasError = false;

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({
          ...prev,
          image: "Please select valid image files (JPEG/PNG)",
        }));
        hasError = true;
        continue;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          image: "Each image should be less than 5MB",
        }));
        hasError = true;
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setImageFiles((prev) => [...prev, ...validFiles]);
      if (!hasError) {
        setErrors((prev) => ({ ...prev, image: "", files: "" }));
      }
    }

    // Reset input
    event.target.value = "";
  };

  const handlePdfUpload = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    // Set upload type to pdf if not set
    if (!uploadType) {
      setUploadType(UPLOAD_TYPE_PDF);
    }

    const validFiles = [];
    let hasError = false;

    for (const file of files) {
      // Validate file type
      if (file.type !== MIME_TYPE_PDF) {
        setErrors((prev) => ({
          ...prev,
          pdf: "Please select valid PDF files",
        }));
        hasError = true;
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          pdf: "Each PDF should be less than 10MB",
        }));
        hasError = true;
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setPdfFiles((prev) => [...prev, ...validFiles]);
      if (!hasError) {
        setErrors((prev) => ({ ...prev, pdf: "", files: "" }));
      }
    }

    // Reset input
    event.target.value = "";
  };

  const removeImageFile = (index) => {
    setImageFiles((prev) => {
      const newFiles = prev.filter((_, i) => i !== index);
      if (newFiles.length === 0) {
        setUploadType(null);
      }
      return newFiles;
    });
  };

  const removePdfFile = (index) => {
    setPdfFiles((prev) => {
      const newFiles = prev.filter((_, i) => i !== index);
      if (newFiles.length === 0) {
        setUploadType(null);
      }
      return newFiles;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!documentType) {
      newErrors.documentType = "Please select document type";
    }

    if (!documentNumber.trim()) {
      newErrors.documentNumber = "Please enter document number";
    }

    if (!amount.trim()) {
      newErrors.amount = "Please enter amount";
    } else if (isNaN(amount) || parseFloat(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
    }

    if (imageFiles.length === 0 && pdfFiles.length === 0) {
      newErrors.files = "Please upload at least one file (Image or PDF)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const convertImagesToPDF = async (images) => {
    try {
      const pdfDoc = await PDFDocument.create();

      for (const imageFile of images) {
        const imageBytes = await imageFile.arrayBuffer();
        let image;

        // Embed image based on type
        if (imageFile.type === MIME_TYPE_IMAGE_JPEG || imageFile.type === MIME_TYPE_IMAGE_JPG) {
          image = await pdfDoc.embedJpg(imageBytes);
        } else if (imageFile.type === MIME_TYPE_IMAGE_PNG) {
          image = await pdfDoc.embedPng(imageBytes);
        } else {
          throw new Error(`Unsupported image type: ${imageFile.type}`);
        }

        // Create a new page with image dimensions
        const page = pdfDoc.addPage([image.width, image.height]);

        // Draw the image on the page
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: image.width,
          height: image.height,
        });
      }

      // Save the PDF as bytes
      const pdfBytes = await pdfDoc.save();

      // Create a Blob from the PDF bytes
      const pdfBlob = new Blob([pdfBytes], { type: MIME_TYPE_PDF });

      // Create a File object from the Blob
      const pdfFile = new File([pdfBlob], "combined_images.pdf", {
        type: MIME_TYPE_PDF,
      });

      return pdfFile;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let finalPdfFile = null;

      // If multiple images are uploaded, convert them to a single PDF
      if (imageFiles.length > 0) {
        finalPdfFile = await convertImagesToPDF(imageFiles);
      } else if (pdfFiles.length > 0) {
        // If PDF is uploaded, use it directly (assuming single PDF for now)
        finalPdfFile = pdfFiles[0];
      }

      if (!finalPdfFile) {
        throw new Error("No document to upload");
      }

      // Get user details from localStorage
      const outletId = window.localStorage.getItem("outlet_id");

      // Prepare FormData for API call
      const formData = new FormData();
      formData.append("entity_number", documentNumber);
      formData.append("entity_type", documentType);
      formData.append("purchase_order_number", purchaseOrderNumber);
      formData.append("amount", parseFloat(amount));
      formData.append("document", finalPdfFile);
      formData.append("user_id", outletId || "");
      formData.append("user_type", USER_TYPE_VENDOR);


      // Call the API
      const response = await api.post(
        API_HOST_WMS,
        APIendpoints.processServiceDoc,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.error && response.data.error.message) {
        setToastMessage(response.data.error.message);
        setIsSuccess(false);
        setShowToast(true);
        setIsSubmitting(false);
        return;
      }

      setShowSuccessModal(true);
    } catch (error) {

      let errorMessage = "Failed to upload documents. Please try again.";
      
      // Check if this is the specific error we need to handle with modal
      if (error?.response?.data?.error?.code === ERROR_CODE_BAD_REQUEST && 
          error?.response?.data?.error?.data) {
        try {
          const errorData = JSON.parse(error.response.data.error.data);
          setErrorModalData({
            message: error.response.data.error.message,
            poAmount: errorData.po_amount,
            totalSrnAmount: errorData.total_srn_amount,
            totalCreditNoteAmount: errorData.total_credit_note_amount,
            remainingAmount: errorData.remaining_amount,
            userName: errorData.user_name,
            userPhone: errorData.user_phone,
          });
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        } catch (parseError) {
          console.error("Failed to parse error data:", parseError);
        }
      }

      if (error?.response?.data) {
        const responseData = error.response.data;

        if (responseData?.error?.message) {
          errorMessage = responseData.error.message;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (typeof responseData?.error === "string") {
          errorMessage = responseData.error;
        } else if (typeof responseData === "string") {
          errorMessage = responseData;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setToastMessage(errorMessage);
      setIsSuccess(false);
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUploadCardTitle = () => {
    if (documentType === ENTITY_TYPE_INVOICE) {
      return "Upload Invoice";
    } else if (documentType === ENTITY_TYPE_CREDIT_NOTE) {
      return "Upload Credit Note";
    }
    return "Upload document";
  };

  const getAmountPlaceholder = () => {
    if (documentType === ENTITY_TYPE_CREDIT_NOTE) {
      return "Credit note amount including taxes";
    }
    return "Invoice amount including taxes";
  };

  return (
    <React.Fragment>
      <Wrapper>
        <ScrollableContent>
          <WhiteWrapper>
            <div className="d-flex align-items-center">
              <BackIcon onClick={() => navigate(-1)}>
                <img
                  src="/logos/back_logo.svg"
                  alt="back"
                  height="20px"
                  width="20px"
                />
              </BackIcon>
              <span className="fs-16 fw-600">
                {purchaseOrderNumber || "Purchase Order"}
              </span>
            </div>
          </WhiteWrapper>

          {/* Document Details Card */}
          <Card>
            <CardTitle>Document details</CardTitle>

            <Dropdown
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              <option value="">Document type</option>
              <option value={ENTITY_TYPE_INVOICE}>INVOICE</option>
              <option value={ENTITY_TYPE_CREDIT_NOTE}>CREDIT_NOTE</option>
            </Dropdown>
            {errors.documentType && (
              <ErrorText>{errors.documentType}</ErrorText>
            )}

            <TextInput
              type="text"
              placeholder="Document number"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
            />
            {errors.documentNumber && (
              <ErrorText>{errors.documentNumber}</ErrorText>
            )}

            <TextInput
              type="number"
              placeholder={getAmountPlaceholder()}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step="0.01"
            />
            {errors.amount && <ErrorText>{errors.amount}</ErrorText>}
          </Card>

          {/* Upload Section Card */}
          <Card>
            <CardTitle>{getUploadCardTitle()}</CardTitle>

            {errors.files && <ErrorText>{errors.files}</ErrorText>}

            <UploadTilesContainer>
              {/* Image Upload Tile */}
              <UploadTile
                hasFile={imageFiles.length > 0}
                disabled={uploadType === UPLOAD_TYPE_PDF}
                onClick={() => {
                  if (uploadType !== UPLOAD_TYPE_PDF) {
                    document.getElementById("image-upload").click();
                  }
                }}
              >
                <input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleImageUpload}
                  multiple
                  disabled={uploadType === UPLOAD_TYPE_PDF}
                />
                <UploadIcon hasFile={imageFiles.length > 0}>📷</UploadIcon>
                <UploadLabel hasFile={imageFiles.length > 0}>
                  {imageFiles.length > 0
                    ? `${imageFiles.length} Image(s)`
                    : "Upload Image"}
                </UploadLabel>
                {imageFiles.length === 0 && (
                  <FileTypeLabel>JPEG/PNG</FileTypeLabel>
                )}
              </UploadTile>

              {/* PDF Upload Tile */}
              <UploadTile
                hasFile={pdfFiles.length > 0}
                disabled={uploadType === UPLOAD_TYPE_IMAGE}
                onClick={() => {
                  if (uploadType !== UPLOAD_TYPE_IMAGE) {
                    document.getElementById("pdf-upload").click();
                  }
                }}
              >
                <input
                  id="pdf-upload"
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  multiple
                  disabled={uploadType === UPLOAD_TYPE_IMAGE}
                />
                <UploadIcon hasFile={pdfFiles.length > 0}>📄</UploadIcon>
                <UploadLabel hasFile={pdfFiles.length > 0}>
                  {pdfFiles.length > 0
                    ? `${pdfFiles.length} PDF(s)`
                    : "Upload PDF"}
                </UploadLabel>
                {pdfFiles.length === 0 && (
                  <FileTypeLabel>PDF</FileTypeLabel>
                )}
              </UploadTile>
            </UploadTilesContainer>

            {errors.image && <ErrorText>{errors.image}</ErrorText>}
            {errors.pdf && <ErrorText>{errors.pdf}</ErrorText>}

            {/* File Previews */}
            {imageFiles.length > 0 && (
              <FilePreviewContainer>
                <PreviewTitle>Image Previews</PreviewTitle>
                {imageFiles.map((file, index) => (
                  <FilePreviewItem key={index}>
                    <ImagePreview
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                    />
                    <FilePreviewInfo>
                      <PreviewFileName>{file.name}</PreviewFileName>
                      <PreviewFileSize>
                        {(file.size / 1024).toFixed(2)} KB
                      </PreviewFileSize>
                    </FilePreviewInfo>
                    <RemoveButton onClick={() => removeImageFile(index)}>
                      ×
                    </RemoveButton>
                  </FilePreviewItem>
                ))}
              </FilePreviewContainer>
            )}

            {pdfFiles.length > 0 && (
              <FilePreviewContainer>
                <PreviewTitle>PDF Previews</PreviewTitle>
                {pdfFiles.map((file, index) => (
                  <FilePreviewItem key={index}>
                    <PdfPreviewIcon>📄</PdfPreviewIcon>
                    <FilePreviewInfo>
                      <PreviewFileName>{file.name}</PreviewFileName>
                      <PreviewFileSize>
                        {(file.size / 1024).toFixed(2)} KB
                      </PreviewFileSize>
                    </FilePreviewInfo>
                    <RemoveButton onClick={() => removePdfFile(index)}>
                      ×
                    </RemoveButton>
                  </FilePreviewItem>
                ))}
              </FilePreviewContainer>
            )}
          </Card>
        </ScrollableContent>

        {/* Fixed Footer with Continue Button */}
        <FixedFooter>
          <SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Uploading..." : "Continue"}
          </SubmitButton>
        </FixedFooter>
      </Wrapper>

      {/* Success Modal */}
      {showSuccessModal && (
        <ModalOverlay
          onClick={() => {
            setShowSuccessModal(false);
            navigate(-1);
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton
              onClick={() => {
                setShowSuccessModal(false);
                navigate(-1);
              }}
            >
              ×
            </CloseButton>
            <SuccessIcon>
              <img
                src="/logos/tick_logo.svg"
                alt="success"
                width="80px"
                height="80px"
              />
            </SuccessIcon>
            <ModalTitle>Invoice uploaded</ModalTitle>
            <ModalButton
              onClick={() => {
                setShowSuccessModal(false);
                navigate(-1);
              }}
            >
              Okay
            </ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}

      {showErrorModal && errorModalData && (
        <ModalOverlay onClick={() => setShowErrorModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowErrorModal(false)}>
              ×
            </CloseButton>
            <ErrorModalContent>
              <ErrorIconContainer>
                <ErrorIconImage src="/logos/cross-circle-fill.svg" alt="Error" />
              </ErrorIconContainer>

              <ErrorMessage>{errorModalData.message}</ErrorMessage>

              <ContactInfoBox>
                <ContactTitle>
                  Please contact Hyperpure PO<br />creator to change PO Amount
                </ContactTitle>
                
                <ContactDetail>
                  <ContactLabel>Creator</ContactLabel>
                  <ContactValue>{errorModalData.userName}</ContactValue>
                </ContactDetail>

                <ContactDetail>
                  <ContactLabel>Creator Number</ContactLabel>
                  <ContactValue>{errorModalData.userPhone}</ContactValue>
                </ContactDetail>
              </ContactInfoBox>

              <ModalButton onClick={() => setShowErrorModal(false)}>
                Close
              </ModalButton>
            </ErrorModalContent>
          </ModalContent>
        </ModalOverlay>
      )}

      {showToast && (
        <ShowToast
          toastMsg={isSuccess ? toastMessage : ""}
          errorMsg={isSuccess ? "" : toastMessage}
        />
      )}
    </React.Fragment>
  );
};

export default UploadDocuments;
